import { BUTTON_SIZE, BUTTON_TYPE, DSButton, TAB_TYPE, Tab } from '@repo/design-system/index'
import { QIcon_check_filled, QIcon_chevron_left } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useBasicPopup } from '../../../../../components/basic/popup/BasicPopup'
import PortalContainer from '../../../../../components/container/PortalContainer'
import MediaPanel from '../../../../../components/material/MediaPanel'
import SectionVoipMembership from '../../../../../components/new-portal/lessons/SectionVoipMembership'
import MaterialRelatedContents from '../../../../../components/new-portal/materials/MaterialRelatedContents'
import PreparationExpressionBody from '../../../../../components/new-portal/materials/PreparationExpressionBody'
import LessonStyleModal from '../../../../../components/student/new-portal/lessons/UpcomingLessonCard/LessonStyleModal'
import { useEventTracking } from '../../../../../helpers/gtmHelper'
import apis from '../../../../../modules/apis'
import { ILessonPreparation } from '../../../../../modules/apis/student/lesson'
import { axiosGet } from '../../../../../modules/axiosHelper'
import { SHOW_ADDITIONAL_FEATURES } from '../../../../../modules/consts/trial'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { n_common, n_lesson_prep, n_upcoming_lessons } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { lessonPreparationQueryApi } from '../../../../../modules/react-query/queryApis'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'

const CONTENT_TAB = {
  MEDIA: 0,
  MATERIAL: 1,
  EXPRESSION: 2,
}

export enum CONTENT_TABS {
  MEDIA = '영상/기사',
  MATERIAL = '링글 관련 콘텐츠',
  EXPRESSION = '핵심표현',
}

export const ContentSubTab = ({ activeTabIdx, setActiveTabIdx }) => {
  const { t } = useTranslation()
  const { sendClickEvent } = useEventTracking()
  const isInitialRender = useRef(true)
  const router = useAppRouter()
  const SUB_MENU_LIST = [
    {
      label: t(n_lesson_prep.complete.tab_video),
    },
    {
      label: t(n_lesson_prep.complete.tab_contents),
    },
    {
      label: t(n_lesson_prep.complete.tab_expression),
    },
  ]

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    if (!router) return
    sendClickEvent({ button_name: 'lesson_done_tab', object_title: SUB_MENU_LIST[activeTabIdx].label })
  }, [activeTabIdx, router])

  return (
    <div className="border-b-1 z-[20] ml-[0px]">
      <div className="">
        <Tab type={TAB_TYPE.FIXED} borderNone={true} activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx}>
          {SUB_MENU_LIST.map((subMenuItem, i) => {
            return <Tab.TabItem name={subMenuItem.label} key={i} />
          })}
        </Tab>
      </div>
    </div>
  )
}

export const ContentBody = ({ lesson, lessonId, activeTabIdx, expressionData, currentHash }) => {
  const media = {
    youtubes: lesson?.course?.video,
    articles: lesson?.course?.article,
  }
  const isMobile = useIsMobile()

  const noMedia = media.youtubes?.length == 0 && media.articles?.length == 0
  const relatedContents = lesson?.course?.related_contents
  return (
    <div className="flex w-full flex-col justify-center">
      {activeTabIdx === CONTENT_TAB.MEDIA && <MediaPanel className="w-full" media={media} noMedia={noMedia} />}
      {activeTabIdx === CONTENT_TAB.MATERIAL && (
        <MaterialRelatedContents relatedContents={relatedContents} isBorderLine={false} />
      )}
      {activeTabIdx === CONTENT_TAB.EXPRESSION && (
        <PreparationExpressionBody expressionData={expressionData} currentHash={currentHash} />
      )}
    </div>
  )
}

const PreparationOver = () => {
  const router = useAppRouter()
  const basicPopup = useBasicPopup()
  const { currentUser } = useCurrentUser()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { sendPageViewEvent, sendClickEvent } = useEventTracking()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!router) return
    if (isLoading) {
      setIsLoading(false)
      return
    }
    sendPageViewEvent({ page_name: 'lesson_prep_done' })
  }, [router.asPath, isLoading])

  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)
  const [lessonStyleSetting, setLessonStyleSetting] = useState(null)
  const [lessonStylePopup, setLessonStylePopup] = useState(false)

  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }

  const { data, error, isFetching } = useQuery<ILessonPreparation, AxiosError>({
    queryKey: ['lesson_preparation_over', lessonId],
    queryFn: lessonPreparationQueryApi.getQueryFn({ pathParams }),
    retry: false,
  })

  const lesson = data?.lesson

  const TopBar = () => {
    return (
      <div className="border-b-1 ml-[0px] hidden h-[60px] bg-white lg:flex lg:w-full">
        <div className="mx-[32px] flex w-full items-center justify-between">
          <div
            className="cursor-pointer"
            onClick={() => {
              router.push(urls.student.newPortal.lessons.preparation.as(lessonId))
              sendClickEvent({ button_name: 'back' })
            }}
          >
            <div className="Body2-14Bd flex flex-row items-center">
              <QIcon_chevron_left />
              <div className="">{t(n_common.btn_back_list)}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 핵심 표현
  const expressionData = lesson?.expressions
  const [currentHash, setCurrentHash] = useState(1)

  const handleLessonStyleClick = async (event) => {
    console.log('lesson style')
    sendClickEvent({ button_name: 'lesson_style' })

    const url = `${apis.lessons.setting(lesson?.id)}`
    const response = await axiosGet(url)
    // setLessonStylePopup(true)
    if (response.success) {
      setLessonStyleSetting(response)
      setLessonStylePopup(true)
    }
  }

  return (
    <PortalContainer layoutClassName="mx-auto mb-[120px] mt-[60px] w-full" noDefaultBar={true} topBar={<TopBar />}>
      <LessonStyleModal
        showPopup={lessonStylePopup}
        setShowPopup={setLessonStylePopup}
        lessonId={lesson?.id}
        isWriting={false} /** writing 수업은 over page 가 없음  */
        response={lessonStyleSetting}
      />
      <div className="mt-[60px] flex w-full flex-col items-center justify-center space-y-20">
        {/** 문구  */}
        <div className="flex flex-col items-center justify-center">
          <QIcon_check_filled className="mb-[12px] h-56 w-56 text-green-500" />
          <div className=" mb-[4px]">
            <span className="text-[16px] font-bold text-green-500">{t(n_lesson_prep.complete.title_done)}</span>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="items-center justify-center text-center text-[28px] font-bold text-black">
              {t(n_lesson_prep.complete.desc(currentUser?.profile?.name))}
            </div>
          </div>
          {!isMobile && (
            <div className="mt-[44px] flex w-full flex-col items-center space-y-[12px]">
              <div className="w-full">
                <DSButton
                  onClick={(e) => handleLessonStyleClick({ event: e, lessonId: lessonId })}
                  size={BUTTON_SIZE.LG}
                  type={BUTTON_TYPE.OUTLINE}
                  customBorderColor={COLORS.GRAY300}
                >
                  <DSButton.Label>{t(n_upcoming_lessons.lesson_style.title)}</DSButton.Label>
                </DSButton>
              </div>
              <div className="w-full">
                <DSButton
                  onClick={() => {
                    router.push(urls.student.newPortal.lessons.upcomingLessons)
                    sendClickEvent({ button_name: 'lesson_done_exit' })
                  }}
                  size={BUTTON_SIZE.LG}
                  type={BUTTON_TYPE.SOLID}
                  customBorderColor={COLORS.PURPLE600}
                >
                  <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
                </DSButton>
              </div>
            </div>
          )}
        </div>

        {/* VoIP 멤버십 섹션 - is_ringle_member는 무조건 표시, 나머지는 constant로 제어 */}
        {(currentUser?.is_ringle_member || SHOW_ADDITIONAL_FEATURES) && (
          <div className={`mx-auto mb-[40px] mt-[24px] w-full max-w-[289px] overflow-y-auto sm:max-w-[720px]`}>
            <SectionVoipMembership />
          </div>
        )}

        {/** 콘텐츠 */}
        <div className="z-60 flex flex-col items-center border-gray-200 bg-white sm:flex sm:w-full">
          <ContentSubTab activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx} />
        </div>

        <div className={`mx-auto mb-[40px] mt-[24px] w-full max-w-[289px] overflow-y-auto sm:max-w-[720px]`}>
          <ContentBody
            lesson={lesson}
            activeTabIdx={activeTabIdx}
            lessonId={lessonId}
            expressionData={expressionData}
            currentHash={currentHash}
          />
          {isMobile && (
            <div
              className={`flex w-full flex-row items-center justify-center gap-[8px] ${
                activeTabIdx !== 0 ? 'mt-[40px]' : 'mt-0'
              } `}
            >
              <div className="w-full">
                <DSButton
                  onClick={(e) => handleLessonStyleClick({ event: e, lessonId: lessonId })}
                  size={BUTTON_SIZE.LG}
                  type={BUTTON_TYPE.OUTLINE}
                  customBorderColor={COLORS.GRAY300}
                >
                  <DSButton.Label>{t(n_upcoming_lessons.lesson_style.title)}</DSButton.Label>
                </DSButton>
              </div>
              <div className="w-full">
                <DSButton
                  onClick={() => {
                    router.push(urls.student.newPortal.lessons.upcomingLessons)
                    sendClickEvent({ button_name: 'lesson_done_exit' })
                  }}
                  size={BUTTON_SIZE.LG}
                  type={BUTTON_TYPE.SOLID}
                  customBorderColor={COLORS.PURPLE600}
                >
                  <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
                </DSButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </PortalContainer>
  )
}

export default PreparationOver
